"use client";
// AppEffects — tüm renderless side-effect provider'ları tek component'te toplar.
// Hiçbiri children wrap etmediği için nesting gerektirmiyordu.
// Birleştirme, provider ağacını 6 katmandan 2'ye indiriyor.
import { useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { searchStorage } from "@/app/lib/storage/searchStorage";
import { sessionStorage } from "@/app/lib/storage/sessionStorage";
import { tokenStorage } from "@/app/lib/api/tokenStorage";
import useAuth from "@/app/lib/features/auth/auth.hook";
import usePopup from "@/app/lib/features/popup/popup.hook";
import { useGenderSelection } from "@/app/hooks/useGenderSelection";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import { getUserProfile } from "@/app/lib/features/auth/auth.thunk";
import { getMenus } from "@/app/lib/features/menus/menus.thunk";
import { getHeader } from "@/app/lib/features/header/header.thunk";
import { getFooter } from "@/app/lib/features/footer/footer.thunk";
import { getCart, getCartCount } from "@/app/lib/features/cart/cart.thunk";
import useCart from "@/app/lib/features/cart/cart.hook";
import { getFavoriteIds } from "@/app/lib/features/favorite/favorite.thunk";
import { markBackForwardNavigation } from "@/app/lib/navigationType";
import { append } from "@/app/lib/features/modal/modal.slice";
import { store } from "@/app/lib/store";

const AppEffects = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { mergeCartData } = useCart();
  // Popup: sadece gerekli alanları granular seç — tüm popup slice dinlenmiyor
  const { isIdle, isSuccess, popup, fetchPopup } = usePopup();

  // Auth initialization — mount'ta token varsa kullanıcı profilini çek
  useEffect(() => {
    const { userProfile } = store.getState().auth;
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken && !userProfile) {
      dispatch(getUserProfile()).unwrap().catch(() => {
        // Profil çekme hatası token'ları silmemeli — 401 durumunu axiosInstance yönetir
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // session_id rotasyonu — sadece misafir kullanıcılar için: TTL dolunca yeni
  // session_id üretip sayfayı yeniler. Auth kullanıcı zaten access token ile
  // kimlikleniyor, session_id'nin onun için bir anlamı yok.
  useEffect(() => {
    const hasToken = isAuthenticated || !!tokenStorage.getAccessToken();
    if (hasToken) return;

    sessionStorage.getSessionId();
    const remaining = sessionStorage.getRemainingTtl();
    if (remaining <= 0) return;

    const timer = setTimeout(() => {
      sessionStorage.refreshSessionId();
      window.location.reload();
    }, remaining);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // Geri/ileri navigasyon işareti — scroll restorasyonunun yalnızca gerçek
  // geri dönüşlerde çalışması için (bkz. lib/navigationType.ts). Root'ta
  // dinleniyor çünkü popstate, hedef sayfa mount olmadan önce tetikleniyor.
  useEffect(() => {
    const onPopState = () => markBackForwardNavigation();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Session initialization — menus, header, footer, cart
  // Thunk'ların kendi condition guard'ları var: veri zaten varsa skip eder.
  useEffect(() => {
    dispatch(getMenus());
    dispatch(getHeader());
    dispatch(getFooter());
    dispatch(getCartCount());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Favori ürün id'leri — sayfalar ISR ile ANONİM üretildiği için SSR HTML'inde
  // `is_favorited` daima false geliyor ve bunu düzelten bir şey yoktu: kullanıcı
  // favorilediği ürünü ana sayfada/listelerde boş kalple görüyordu. Tek bir
  // auth'lu istekle id listesi çekilip Redux'a yazılıyor, FavoriteButton oradan
  // okuyor. initializeFavoriteState yalnızca EKLEDİĞİ için (hiç silmez) anonim
  // false değeri bu listeyi ezmiyor — sıralama önemli değil.
  // Token kontrolü şart: misafirde 401 → refresh → login yönlendirme döngüsü olur.
  useEffect(() => {
    const hasToken = isAuthenticated || !!tokenStorage.getAccessToken();
    if (!hasToken) return;
    dispatch(getFavoriteIds());
  }, [isAuthenticated, dispatch]);

  // Auth transition — login/logout sonrası home server component'lerini yenile
  const wasAuthenticatedAtMount = useRef(!!tokenStorage.getAccessToken());
  const prevIsAuthenticated = useRef(isAuthenticated);
  // Giriş sonrası misafir sepetini kullanıcı sepetine taşı.
  //
  // Eskiden burada doğrudan `dispatch(mergeCart())` çağrılıyordu. Ama
  // `mergeCart.fulfilled` yalnızca status yazıyor — ne `cart.data`'yı ne de
  // `cart.count`'u güncelliyor. Üstelik misafir `session_id`'si de
  // temizlenmiyordu. Sonuç: rozet misafir sepetindeki eski sayıyı (ör. 3)
  // göstermeye devam ediyor, /sepet açılıp gerçek veri çekilince "sepetiniz
  // boş" çıkıyordu.
  //
  // `mergeCartData` doğru sırayı zaten uyguluyor: merge → clearSessionId →
  // getCart (data + count birlikte güncelleniyor).
  const handleAuthTransition = useCallback(async () => {
    const merged = await mergeCartData();
    if (!merged) {
      // Merge başarısız olsa bile sepeti sunucudan tazele; aksi hâlde ekranda
      // misafir sepeti kalır ve gerçekle uyuşmaz.
      dispatch(getCart());
    }
    searchStorage.clearSearches();
  }, [mergeCartData, dispatch]);

  useEffect(() => {
    const prev = prevIsAuthenticated.current;
    prevIsAuthenticated.current = isAuthenticated;

    // Mount'ta değişim yok — sadece gerçek geçişleri yakala
    if (prev === isAuthenticated) return;

    // Login geçişi
    if (isAuthenticated && !wasAuthenticatedAtMount.current) {
      handleAuthTransition();
      router.refresh();
      return;
    }

    // Logout geçişi
    if (!isAuthenticated && prev) {
      router.refresh();
    }
  }, [isAuthenticated, handleAuthTransition, router]);

  // Gender selection
  useGenderSelection();

  // Popup
  useEffect(() => {
    if (isIdle && isAuthenticated) {
      fetchPopup();
    }
  }, [isIdle, isAuthenticated, fetchPopup]);

  useEffect(() => {
    if (isSuccess && popup) {
      dispatch(append({ name: "popup" }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, popup]);

  return null;
};

export default AppEffects;